import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    let active = true;
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      try {
        const r = await getPaymentStatus(sessionId);
        if (!active) return;
        if (r.payment_status === "paid") {
          clear();
          setState("paid");
          setTimeout(() => { if (active && r.order_id) navigate(`/order/${r.order_id}`); }, 1200);
          return;
        }
        if (attempts.current >= 8) { setState("timeout"); return; }
        attempts.current += 1;
        setTimeout(poll, 2000);
      } catch {
        if (!active) return;
        if (attempts.current >= 8) { setState("error"); return; }
        attempts.current += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="animate-spin" size={32} />
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">{lang === "ro" ? "Confirmăm plata…" : "Confirming payment…"}</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={40} />
          <h1 className="mt-4 text-2xl uc font-semibold" data-testid="payment-paid">{lang === "ro" ? "Plată reușită" : "Payment successful"}</h1>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">{lang === "ro" ? "Îți mulțumim! Te redirecționăm…" : "Thank you! Redirecting…"}</p>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <h1 className="text-2xl uc font-semibold">{lang === "ro" ? "Plata se procesează" : "Payment processing"}</h1>
          <p className="mt-2 max-w-md font-mono text-xs uppercase leading-relaxed tracking-widest text-muted-foreground">
            {lang === "ro" ? "Vei primi un email de confirmare imediat ce plata este confirmată." : "You'll receive a confirmation email once the payment clears."}
          </p>
          <Link to="/" className="mt-6 border border-foreground px-6 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-white">{lang === "ro" ? "Înapoi la magazin" : "Back to shop"}</Link>
        </>
      )}
    </main>
  );
};

export default PaymentSuccess;
